import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { customerActivityTypes } from 'constants/customer-account.constants';
import { getSupportRequests, getSupportRequestsPagination } from 'state/reducers/customer-accounts.reducers';
import { getCompanySupportRequests } from 'services/customer-accounts.service';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

const INITIAL_SORT_BY = 'createdAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

export const SupportRequestsTable: React.FC = () => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const { id } = useParams();
	const supportRequests = useAppSelector(getSupportRequests);
	const pagination = useAppSelector(getSupportRequestsPagination);
	const [tableData, setTableData] = useState<Record<string, any>[]>([]);
	const sortFieldMap: Record<string, string> = {
		createdAt: 'created_at',
		subject: 'subject',
		status: 'status',
		updatedAt: 'updated_at',
		boNotes: 'boNotes',
		reference: 'reference',
	};
	const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

	const fetchSupportRequests = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			if (!id) return;
			await dispatch(
				getCompanySupportRequests(parseInt(id), {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
				})
			);
		},
		[id, dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchSupportRequests,
	});

	useEffect(() => {
		if (!id) return;
		runFetch(0);
	}, [id, runFetch]);

	useEffect(() => {
		setTableData(supportRequests || []);
	}, [supportRequests]);

	const handleSortChangeWithState = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder),
		[handleSortChange]
	);

	return (
		<Stack spacing={2}>
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{pagination.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowsClass="alignTop"
				data={TableConfig.tableData(tableData)}
				defaultSortBy={INITIAL_SORT_BY}
				defaultOrderBy={INITIAL_SORT_ORDER}
				columns={TableConfig.buildColumns(
					customerActivityTypes.SUPPORT_REQUESTS,
					t,
					dispatch,
					tableData,
					setTableData
				)}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: pagination.total,
					hasMore: pagination.hasMore,
					currentPage,
					onPageChange: handlePageChange,
					onSortChange: handleSortChangeWithState,
					isLoading,
				}}
			/>
		</Stack>
	);
};
